import React, { useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useBooking } from '../context/BookingContext';
import './Pricing.css';

/** Pay-in-full discount; `projectMin` / `projectMax` on each tier are the upfront totals after this discount. */
const UPFRONT_DISCOUNT = 0.1;

const fmt = (n) => `$${n.toLocaleString('en-US')}`;

/** Implied full project price before the pay-in-full discount (used for 12-month totals). */
function listPriceFromUpfront(upfront) {
  return upfront / (1 - UPFRONT_DISCOUNT);
}

/** Round each monthly installment endpoint to the nearest $25 so ranges read cleanly. */
function niceMonthlyRange(listMin, listMax) {
  const round25 = (x) => Math.round(x / 25) * 25;
  return {
    low: round25(listMin / 12),
    high: round25(listMax / 12),
  };
}

const oneTimePackages = [
  {
    id: 'discovery',
    name: 'Free Discovery Session',
    pricingMode: 'free',
    price: 'Free',
    cadence: '',
    eyebrow: 'Where every engagement starts',
    tagline: 'We map how you handle leads, follow-up, and daily work, and what it costs.',
    description:
      'A 45-60 minute session. No charge. No obligation. If we don’t see a clear opportunity, we’ll tell you.',
    highlights: [
      'Map inquiries, follow-up, and daily operations',
      'Surface gaps and what they cost',
      'Clear read on what we’d fix first',
    ],
    cta: 'Book a Discovery Session',
    popular: false,
    variant: 'discovery',
  },
  {
    id: 'pilot',
    name: 'The Pilot',
    pricingMode: 'project',
    /** Pay-in-full (10% off list); monthly = list ÷ 12. */
    projectMin: 4500,
    projectMax: 6000,
    tagline: 'The fastest way to see results.',
    description:
      'We take what matters most from discovery and ship 2-3 workflows, built properly, deployed in your systems, live within two weeks. Most clients recover the full cost within the first month.',
    highlights: [
      'Full written audit: gaps, dollar values, recommendations',
      '2-3 workflows built, tested, and deployed in your existing systems',
      'Team walkthrough (no technical knowledge required)',
      '14 days of support after handoff',
    ],
    bestFor:
      'Businesses ready to put 2-3 workflows live fast: missed calls, no follow-up, leads going cold, manual reminders eating staff time.',
    cta: 'Start with the Pilot',
    badge: 'Most Popular',
    popular: true,
  },
  {
    id: 'pro',
    name: 'The Pro',
    pricingMode: 'project',
    /** Pay-in-full (10% off list); monthly = list ÷ 12. */
    projectMin: 8000,
    projectMax: 12000,
    tagline: 'Full build, including your Custom AI Assistant.',
    description:
      'A full system built around an assistant trained on your services, tone, and how you work, plus 5 to 7 workflows for your biggest gaps.',
    highlights: [
      'Full written audit report',
      'Custom AI Assistant, trained on your business',
      '5-7 workflows built and deployed',
      'Integrated with the tools you already use',
      'Full team training and handoff',
      '30 days of support after delivery',
    ],
    bestFor:
      'Shops where fast, consistent replies to clients and staff matter for revenue: medspas, firms, brokers, clinics, gyms, real estate, dealerships, trades, and similar.',
    cta: 'Talk About Pro',
    popular: false,
  },
];

function projectPriceDisplay(pkg, billing) {
  const upfrontLow = pkg.projectMin;
  const upfrontHigh = pkg.projectMax;
  const listLow = listPriceFromUpfront(upfrontLow);
  const listHigh = listPriceFromUpfront(upfrontHigh);
  const { low: monthlyLow, high: monthlyHigh } = niceMonthlyRange(listLow, listHigh);

  if (billing === 'monthly') {
    return {
      primary: `${fmt(monthlyLow)}–${fmt(monthlyHigh)}/mo`,
      cadence: '12 payments',
      note: null,
      stackPrice: true,
      cadenceKind: 'installment',
    };
  }

  return {
    primary: `${fmt(upfrontLow)}–${fmt(upfrontHigh)}`,
    cadence: 'One payment',
    note: null,
    stackPrice: true,
    cadenceKind: 'upfront',
  };
}

const OneTimeCard = ({ pkg, billing }) => {
  const { openCalendly } = useBooking();

  const priceBlock =
    pkg.pricingMode === 'free'
      ? { primary: pkg.price, cadence: pkg.cadence, note: null, stackPrice: false, cadenceKind: null }
      : projectPriceDisplay(pkg, billing);

  const stackedProject = pkg.pricingMode === 'project' && priceBlock.stackPrice;

  return (
    <article
      className={`plan-card ${pkg.popular ? 'plan-card-popular' : ''} ${pkg.variant ? `plan-card-${pkg.variant}` : ''} ${pkg.id === 'pro' ? 'plan-card-pro' : ''} ${stackedProject ? 'plan-card--stacked-price' : ''}`}
    >
      {pkg.badge && <span className="plan-badge">{pkg.badge}</span>}
      <div className="plan-header">
        <h3 className="plan-name">{pkg.name}</h3>
        {pkg.eyebrow && <p className="plan-eyebrow">{pkg.eyebrow}</p>}
        <div
          className={`plan-price-row ${priceBlock.stackPrice ? 'plan-price-row--stack' : ''}`}
        >
          <span className="plan-price">{priceBlock.primary}</span>
          {priceBlock.cadence ? (
            <span
              className={
                priceBlock.stackPrice && priceBlock.cadenceKind
                  ? `plan-cadence plan-cadence--pill plan-cadence--pill-${priceBlock.cadenceKind}`
                  : 'plan-cadence'
              }
            >
              {priceBlock.cadence}
            </span>
          ) : null}
        </div>
        {priceBlock.note && <p className="plan-price-note">{priceBlock.note}</p>}
      </div>
      <p className="plan-tagline">{pkg.tagline}</p>
      <p className="plan-description">{pkg.description}</p>
      <ul className="plan-features">
        {pkg.highlights.map((item) => (
          <li key={item}>
            <Check size={14} className="plan-check" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      {pkg.bestFor && (
        <p className="plan-best-for">
          <strong>Best for:</strong> {pkg.bestFor}
        </p>
      )}
      <button
        type="button"
        className={`btn plan-cta ${pkg.popular ? 'btn-primary' : 'btn-secondary'}`}
        onClick={openCalendly}
      >
        {pkg.cta}
        <ArrowRight size={15} />
      </button>
    </article>
  );
};

const Pricing = () => {
  const [billing, setBilling] = useState('annual');
  const [headRef, headVisible] = useScrollReveal();
  const [row1Ref, row1Visible] = useScrollReveal();
  const [assistantRef, assistantVisible] = useScrollReveal();

  return (
    <section id="pricing" className="pricing section">
      <div className="container">
        <div ref={headRef} className={`pricing-headline reveal ${headVisible ? 'is-visible' : ''}`}>
          <p className="section-kicker">Packages</p>
          <h2 className="pricing-main-headline">
            Simple, honest pricing.
            <br />
            <span>Start where it makes sense for you.</span>
          </h2>
          <p className="pricing-subheadline">
            Not a receptionist. One assistant for clients and staff. It handles messages, routes updates when things
            change, and sends announcements. WhatsApp, SMS, email, and the tools you already use.
          </p>
          <div className="pricing-billing-wrap">
            <p className="pricing-billing-intro">
              Pay upfront and save 10% — or spread the full project over 12 monthly payments.
            </p>
            <div className="pricing-billing-toggle" role="group" aria-label="Payment style">
              <button
                type="button"
                className={`pricing-billing-btn ${billing === 'monthly' ? 'is-active' : ''}`}
                onClick={() => setBilling('monthly')}
                aria-pressed={billing === 'monthly'}
              >
                Monthly
                <span className="pricing-billing-hint">12 payments</span>
              </button>
              <button
                type="button"
                className={`pricing-billing-btn ${billing === 'annual' ? 'is-active' : ''}`}
                onClick={() => setBilling('annual')}
                aria-pressed={billing === 'annual'}
              >
                Pay upfront
                <span className="pricing-billing-hint">10% off</span>
              </button>
            </div>
          </div>
        </div>

        <div ref={row1Ref} className={`pricing-row reveal ${row1Visible ? 'is-visible' : ''}`}>
          <div className="pricing-card-grid pricing-card-grid-3">
            {oneTimePackages.map((pkg) => (
              <OneTimeCard key={pkg.id} pkg={pkg} billing={billing} />
            ))}
          </div>
        </div>

        <div ref={assistantRef} className={`pricing-assistant-row reveal ${assistantVisible ? 'is-visible' : ''}`}>
          <div className="pricing-assistant-label">
            <span>Custom AI Assistant</span>
            <p>
              Included with <strong>The Pro</strong>. Trained on your business. Same voice for client chat and team
              updates on WhatsApp, SMS, and email.
            </p>
          </div>
        </div>

        <div className="pricing-credit-note">
          <p>
            Every engagement starts with a discovery session. If you move forward with <strong>Pro</strong> after the{' '}
            <strong>Pilot</strong>, the Pilot investment is <strong>credited toward Pro</strong>.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
